import type { StateCreator } from 'zustand'
import {
  listAgentSchedules,
  patchAgentSchedule,
  type AgentSchedule,
  type AgentSchedulePatch,
} from '@/shared/lib/api'

export interface AgentSchedulesSlice {
  agentSchedules: AgentSchedule[]
  agentSchedulesLoading: boolean
  agentSchedulesError: string | null

  fetchAgentSchedules: () => Promise<void>
  updateAgentSchedule: (id: number, patch: AgentSchedulePatch) => Promise<void>
}

export const createAgentSchedulesSlice: StateCreator<
  AgentSchedulesSlice,
  [],
  [],
  AgentSchedulesSlice
> = (set, get) => ({
  agentSchedules: [],
  agentSchedulesLoading: false,
  agentSchedulesError: null,

  fetchAgentSchedules: async () => {
    set({ agentSchedulesLoading: true, agentSchedulesError: null })
    try {
      const schedules = await listAgentSchedules()
      set({ agentSchedules: schedules, agentSchedulesLoading: false })
    } catch (err) {
      set({
        agentSchedulesError:
          err instanceof Error ? err.message : 'Failed to fetch agent schedules',
        agentSchedulesLoading: false,
      })
    }
  },

  updateAgentSchedule: async (id, patch) => {
    // Optimistic update
    const previous = get().agentSchedules
    set({
      agentSchedules: previous.map((s) =>
        s.id === id ? { ...s, ...patch } : s
      ),
    })
    try {
      const updated = await patchAgentSchedule(id, patch)
      set({
        agentSchedules: get().agentSchedules.map((s) =>
          s.id === id ? updated : s
        ),
      })
    } catch (err) {
      // Rollback on error
      set({
        agentSchedules: previous,
        agentSchedulesError:
          err instanceof Error ? err.message : 'Failed to update agent schedule',
      })
      throw err
    }
  },
})
